import { useEffect, useState } from 'react'

import { AppleLogo, GoogleLogo, LockupWhite } from '@/assets'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import Label from '@/elements/Label'
import Flex from '@/elements/Flex'
import FormToggle from '@/elements/FormToggle'
import EmailPasswordLogin from '@/components/EmailPasswordLogin'
import { startAuth } from '@/stores/authStore'
import { saveInviteFlowParams } from '@/utils/inviteFlow'
import { getKeepMeLoggedIn, setKeepMeLoggedIn } from '@/utils/keepMeLoggedIn'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useTheme } from '@/utils/theme'


function Login() {
  const { isMobile } = useBreakpoints()
  const { theme } = useTheme()
  const [keepLogin, setKeepLogin] = useState(getKeepMeLoggedIn())
  const [loginError, setLoginError] = useState<string | null>(null)

  const handleError = (message: string) => {
    setLoginError(message)
    setTimeout(() => setLoginError(null), 2500)
  }

  useEffect(() => {
    setKeepMeLoggedIn(keepLogin)
  }, [keepLogin])

  useEffect(() => {
    saveInviteFlowParams()
    // Only runs once
  }, [])

  return (
    <Flex
      component='main'
      align='center'
      justify='center'
      gap={!isMobile ? 6 : 4}
      py={!isMobile ? 7 : 5}
      px={4}
      className='auth'
      style={{ minHeight: '100svh' }}
    >
      <LockupWhite
        style={{
          width: 162,
          maxWidth: '80%',
          filter: theme === 'dark' ? 'invert(0)' : 'invert(1)'
        }}
      />
      <Flex
        align='center'
        gap={4}
        grow
        style={{
          width: !isMobile ? 306 : '100%',
          maxWidth: '100%'
        }}
      >
        <Flex align='center' style={{ marginBottom: 1 }}>
          <Text variant='h1' style={{ fontSize: '2rem' }}>Welcome back!</Text>
          <Text variant='textblock' color='black-light-10' align='center'>Log in to continue to your account</Text>
        </Flex>

        <div style={{ position: 'relative', height: 0, width: '100%' }}>
          {loginError && (
            <div style={{
              position: 'absolute',
              top: -20,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--color-red)',
              borderRadius: 9999,
              padding: '10px 20px',
              whiteSpace: 'nowrap',
              animation: 'bannerFlash 2.5s ease forwards',
            }}>
              <Text variant='callout' style={{ color: 'var(--color-white)', fontWeight: 600, fontSize: '0.7rem' }}>
                {loginError}
              </Text>
            </div>
          )}
        </div>

        <Flex gap={2} wfull>
          <EmailPasswordLogin onError={handleError} flashError={!!loginError} />
        </Flex>
        <Flex gap={3} align='center'>
          <Flex inline align='center' gap={3} wfull>
            <Flex style={{ height: 1 }} color='gray-light-20' wfull />
            <Text variant='callout' color='black-light-60' style={{ whiteSpace: 'nowrap' }}>or</Text>
            <Flex style={{ height: 1 }} color='gray-light-20' wfull />
          </Flex>
          <Button
            className='button'
            onClick={() => startAuth('apple')}
            styles={{ width: '100%' }}
            variant={'secondary'}
          >
            <AppleLogo width={30} height={30} />
            Log in with Apple ID
          </Button>
          <Button
            className='button'
            onClick={() => startAuth('google')}
            styles={{ width: '100%' }}
            variant={'secondary'}
          >
            <GoogleLogo width={22} height={22} />
            Log in with Google
          </Button>
          <Flex style={{ height: 1, marginTop: 8 }} color='gray-light-20' wfull />
          {/* TODO: add link to terms and privacy once they exist */}
          <Flex inline justify='space-between' align='center' wfull>
            <Label htmlFor='keepMeLoggedIn' position='left'>Keep me logged in</Label>
            <FormToggle
              value={keepLogin}
              name='keepMeLoggedIn'
              className='login-keep-me-logged-in'
              onChange={({ target }) => setKeepLogin(target.checked)}
            />
          </Flex>
          <Text variant='textblock' style={{ color: 'var(--color-terms-of-service-main)' }} align='center'>
             By continuing, you agree to our{' '}
            <span style={{ color: 'var(--color-terms-of-service)' }}>Terms of Service</span>
            {' '}and{' '}
            <span style={{ color: 'var(--color-terms-of-service)' }}>Privacy Policy</span>.
          </Text>
        </Flex>
      </Flex>
    </Flex >
  )
}

export default Login
